import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Camera, Users, ShoppingBag, Gamepad2, Ticket, Dog, PenSquare, Brain, Star, Trophy, HelpCircle, Settings, Wallet, ChevronRight, Globe2, Gift, MessageSquare, Diamond, Shield, RotateCcw, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { isNativeMobile, restorePurchases } from '../lib/iap';
import { toast } from 'sonner';

const FEATURED = [
  { icon: Heart, label: 'Dating', to: '/app/dating', color: 'from-rose-500 to-pink-600' },
  { icon: Camera, label: 'Avatar', to: '/app/profile', color: 'from-cyan-500 to-blue-600' },
  { icon: Users, label: 'Planets', to: '/app/planets', color: 'from-violet-500 to-fuchsia-600' },
];

const BIG_CARDS = [
  { icon: ShoppingBag, label: 'Shop & Gifts', desc: 'Send animated gifts to travelers across the cosmos.', tag: 'NEW', to: '/app/gifts', color: 'from-orange-500 to-amber-500' },
  { icon: Gamepad2, label: 'Games', desc: 'Quiz Rooms, Tic Tac Toe and Battleship — win Stars.', tag: 'NEW', to: '/app/games', color: 'from-blue-500 to-indigo-600' },
  { icon: Ticket, label: 'Daily Reward', desc: 'Open your daily chest, collect login streak bonuses.', tag: 'FREE', to: '/app', color: 'from-emerald-500 to-teal-600' },
];

const GRID = [
  { icon: Dog, label: 'Pets', to: '/app/pets', color: '#FB923C', badge: 'NEW' },
  { icon: Diamond, label: 'Dating', to: '/app/dating', color: '#F472B6' },
  { icon: Gamepad2, label: 'Games', to: '/app/games', color: '#06B6D4' },
  { icon: Trophy, label: 'Leaderboard', to: '/app/leaderboard', color: '#FBBF24', badge: 'NEW' },
  { icon: Star, label: 'Cosmic Ranks', to: '/app/ranks', color: '#A78BFA' },
  { icon: HelpCircle, label: 'Help', to: '/app/help', color: '#C084FC' },
  { icon: Settings, label: 'Settings', to: '/app/profile', color: '#A8A8B3' },
  { icon: Wallet, label: 'Top up', action: 'buy-stars', color: '#34D399' },
  { icon: Gift, label: 'Gifts', to: '/app/gifts', color: '#F97316' },
  { icon: MessageSquare, label: 'Mails', to: '/app/messages', color: '#00C6FF' },
  { icon: Globe2, label: 'Chat', to: '/app/planets', color: '#7B2FF7' },
  { icon: PenSquare, label: 'Feed', to: '/app/feed', color: '#14B8A6' },
];

export default function Menu() {
  const { user, openBuyStars, refresh } = useAuth();
  const [restoring, setRestoring] = useState(false);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await restorePurchases();
      // Any restored purchases fire the plugin's "approved" hook, which our
      // iap.js service verifies against the backend and credits idempotently.
      // Refresh the wallet so any newly-granted VIP / stars appear instantly.
      setTimeout(() => refresh(), 1500);
      toast.success('Restore complete — any prior purchases have been re-applied.');
    } catch (e) {
      toast.error(e?.message || 'Could not restore purchases.');
    } finally {
      setRestoring(false);
    }
  };

  const native = isNativeMobile();
  const isAdult = !!user?.is_adult;
  // Dating tiles (Featured + Grid) are stripped for under-18 users. Server
  // enforces the same rule; hiding here just keeps the UI truthful so a
  // minor never taps a card that would 403.
  const featured = FEATURED.filter(f => f.label !== 'Dating' || isAdult);
  const gridDatingFiltered = GRID.filter(g => g.label !== 'Dating' || isAdult);
  const grid = [
    ...(user?.is_admin ? [{ icon: Shield, label: 'Admin', to: '/app/admin', color: '#F472B6', badge: 'ADMIN' }] : []),
    ...gridDatingFiltered,
    // Restore Purchases — mandatory affordance for the App Store; also handy
    // on Google Play. Only shown inside the native shell where IAP is active.
    ...(native ? [{ icon: RotateCcw, label: 'Restore Purchases', action: 'restore-purchases', color: '#38BDF8' }] : []),
  ];
  return (
    <div className="space-y-5">
      <h1 className="font-display font-bold text-2xl md:text-3xl">Menu</h1>

      <div className="grid grid-cols-3 gap-3">
        {featured.map(f => {
          const Icon = f.icon;
          return (
            <Link key={f.label} to={f.to} className="group rounded-2xl p-4 bg-[#161622] border border-white/10 hover:border-white/20 hover:-translate-y-0.5 text-center">
              <div className={`w-12 h-12 mx-auto rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                <Icon className="text-white" size={20} />
              </div>
              <div className="text-xs font-medium mt-2 truncate">{f.label}</div>
            </Link>
          );
        })}
      </div>

      <div className="space-y-3">
        {BIG_CARDS.map(b => {
          const Icon = b.icon;
          return (
            <Link key={b.label} to={b.to} className="block group">
              <Card className="bg-[#161622] border-white/10 overflow-hidden hover:-translate-y-0.5">
                <CardContent className="p-0 flex items-center">
                  <div className={`w-28 h-24 sm:w-36 sm:h-28 bg-gradient-to-br ${b.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="text-white" size={42} />
                  </div>
                  <div className="flex-1 px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-bold text-lg">{b.label}</span>
                      {b.tag && <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${b.tag === 'NEW' ? 'bg-rose-500 text-white' : 'bg-sky-400 text-black'}`}>{b.tag}</span>}
                    </div>
                    <div className="text-xs text-white/60 mt-1 line-clamp-2">{b.desc}</div>
                  </div>
                  <ChevronRight className="text-white/40 mr-4" size={18} />
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {grid.map(g => {
          const Icon = g.icon;
          const inner = (
            <>
              {g.badge && <span className="absolute top-1.5 right-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white">{g.badge}</span>}
              <div className="w-12 h-12 mx-auto flex items-center justify-center"><Icon size={28} style={{ color: g.color }} /></div>
              <div className="text-xs font-medium mt-2 truncate">{g.label}</div>
            </>
          );
          const className = "group relative rounded-2xl p-4 bg-[#161622] border border-white/10 hover:border-white/20 hover:-translate-y-0.5 text-center";

          // Action entries open a dialog / trigger side-effect instead of navigating
          if (g.action === 'buy-stars') {
            return (
              <button
                key={g.label}
                type="button"
                onClick={openBuyStars}
                data-testid={`menu-${g.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={className + ' w-full'}
              >
                {inner}
              </button>
            );
          }
          if (g.action === 'restore-purchases') {
            return (
              <button
                key={g.label}
                type="button"
                onClick={handleRestore}
                disabled={restoring}
                data-testid="menu-restore-purchases"
                className={className + ' w-full disabled:opacity-70'}
              >
                {restoring ? (
                  <>
                    <div className="w-12 h-12 mx-auto flex items-center justify-center"><Loader2 size={28} className="animate-spin" style={{ color: g.color }} /></div>
                    <div className="text-xs font-medium mt-2">Restoring…</div>
                  </>
                ) : inner}
              </button>
            );
          }

          return (
            <Link key={g.label} to={g.to} data-testid={`menu-${g.label.toLowerCase()}`} className={className}>
              {inner}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
